import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CalendarDays, RefreshCw, Users, LogIn, LogOut, AlertTriangle, CircleDot, Truck, BellRing, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtT = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtNum = (n) => Number(n || 0).toLocaleString('en-ZA');
const hoursBetween = (a, b) => {
  if (!a || !b) return null;
  return Math.max(0, (new Date(b) - new Date(a)) / 3600000);
};

const roleBadge = (r) => {
  if (r === 'driver') return <Badge variant="outline" className="border-indigo-300 text-indigo-700 bg-indigo-50">Owner Driver</Badge>;
  if (r === 'internal_staff') return <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Internal Staff</Badge>;
  return <Badge variant="outline">{(r || '').replace('_', ' ')}</Badge>;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function DailyAttendance() {
  const [shiftDate, setShiftDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {shift_id,name,vehicle,start_km,end_km}
  const [editForm, setEditForm] = useState({ start_km: '', end_km: '', admin_note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/driver-shifts/daily-attendance`, {
        params: { shift_date: shiftDate },
      });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [shiftDate]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      start_km: r.start_km ?? '',
      end_km: r.end_km ?? '',
      admin_note: '',
    });
  };

  const submitEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.start_km !== '' && String(editForm.start_km) !== String(editing.start_km ?? '')) {
        payload.start_km = Number(editForm.start_km);
      }
      if (editForm.end_km !== '' && String(editForm.end_km) !== String(editing.end_km ?? '')) {
        payload.end_km = Number(editForm.end_km);
      }
      if (editForm.admin_note.trim()) payload.admin_note = editForm.admin_note.trim();
      if (Object.keys(payload).length === 0) {
        toast.error('No changes to save');
        setSaving(false);
        return;
      }
      await axios.put(`${API}/driver-shifts/${editing.shift_id}`, payload);
      toast.success('Shift updated · fleet mileage re-synced');
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const rows = data?.clocked_in || [];
  const missing = data?.not_clocked_in || [];
  const totals = data?.totals || {};

  const hoursSum = useMemo(() => {
    return rows.reduce((acc, r) => acc + (hoursBetween(r.clock_in_time, r.clock_out_time || new Date()) || 0), 0);
  }, [rows]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-indigo-600" /> Daily Attendance
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Clock-in / clock-out times for every driver and staff member on the selected day — and who didn't clock in.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[11px]">Date</Label>
              <Input
                type="date"
                value={shiftDate}
                onChange={(e) => setShiftDate(e.target.value)}
                className="h-9 w-44"
                max={todayStr()}
                data-testid="attendance-date"
              />
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="attendance-refresh">
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              variant="outline" size="sm"
              className="border-amber-400 text-amber-800 hover:bg-amber-50"
              data-testid="ping-admins-btn"
              onClick={async () => {
                try {
                  const r = await axios.post(`${API}/driver-shifts/scan-missed-clocks`, null, { params: { shift_date: shiftDate } });
                  const { missed, notifications_sent } = r.data || {};
                  if (missed && missed > 0) toast.success(`Pinged ${notifications_sent} admin bell(s) about ${missed} missed clock-in${missed === 1 ? '' : 's'}`);
                  else toast.info('Nobody missed a clock-in on this day.');
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
              }}
              disabled={loading}
            >
              <BellRing className="h-3 w-3 mr-1" /> Ping Admins Now
            </Button>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="attendance-kpis">
          <Tile label="Expected" value={totals.expected ?? '—'} icon={<Users className="h-4 w-4 text-slate-500" />} />
          <Tile label="Clocked In" value={totals.clocked_in ?? '—'} icon={<LogIn className="h-4 w-4 text-emerald-600" />} highlight="emerald" />
          <Tile label="Still on Duty" value={totals.still_on_duty ?? '—'} icon={<CircleDot className="h-4 w-4 text-amber-600" />} highlight={totals.still_on_duty ? 'amber' : ''} />
          <Tile label="Completed" value={totals.completed ?? '—'} icon={<LogOut className="h-4 w-4 text-slate-500" />} />
          <Tile label="Did Not Clock" value={totals.not_clocked_in ?? '—'} icon={<AlertTriangle className="h-4 w-4 text-rose-600" />} highlight={totals.not_clocked_in ? 'rose' : ''} />
        </div>

        {/* Clocked-in table */}
        <Card data-testid="clocked-in-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <LogIn className="h-4 w-4 text-emerald-600" /> Clocked In ({rows.length})
            </CardTitle>
            <CardDescription className="text-xs">
              Total hours logged: <b>{hoursSum.toFixed(1)} h</b>
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="text-center py-6 text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">Nobody has clocked in yet on {shiftDate}.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-center">Clock In</TableHead>
                    <TableHead className="text-center">Clock Out</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Start KM</TableHead>
                    <TableHead className="text-right">End KM</TableHead>
                    <TableHead className="text-right">Distance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Fix</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const hrs = hoursBetween(r.clock_in_time, r.clock_out_time);
                    const stillOn = r.status === 'in_progress';
                    return (
                      <TableRow key={r.driver_id} data-testid={`row-in-${r.driver_id}`}>
                        <TableCell className="font-semibold">{r.name}</TableCell>
                        <TableCell>{roleBadge(r.role)}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {r.vehicle
                            ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">🚚 {r.vehicle}</span>
                            : <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell className="text-center font-mono text-emerald-700">{fmtT(r.clock_in_time)}</TableCell>
                        <TableCell className="text-center font-mono text-slate-700">
                          {stillOn
                            ? <span className="text-amber-600 flex items-center gap-1 justify-center"><CircleDot className="h-3 w-3 animate-pulse" /> on duty</span>
                            : fmtT(r.clock_out_time)}
                        </TableCell>
                        <TableCell className="text-right font-mono">{hrs !== null ? hrs.toFixed(1) + ' h' : '—'}</TableCell>
                        <TableCell className="text-right font-mono" data-testid={`start-km-${r.driver_id}`}>
                          {r.start_km != null ? fmtNum(r.start_km) : <span className="text-rose-500">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono" data-testid={`end-km-${r.driver_id}`}>
                          {r.end_km != null ? fmtNum(r.end_km) : <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {r.distance_km ? fmtNum(r.distance_km) + ' km' : '—'}
                        </TableCell>
                        <TableCell>
                          {stillOn
                            ? <Badge className="bg-emerald-600">In Progress</Badge>
                            : <Badge className="bg-slate-500">Completed</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.shift_id ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 gap-1"
                              onClick={() => openEdit(r)}
                              data-testid={`edit-shift-${r.driver_id}`}
                            >
                              <Pencil className="h-3 w-3" /> Edit KM
                            </Button>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Not-clocked-in table */}
        <Card data-testid="not-clocked-card" className={missing.length ? 'border-rose-200' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${missing.length ? 'text-rose-600' : 'text-slate-400'}`} />
              Did Not Clock In ({missing.length})
            </CardTitle>
            <CardDescription className="text-xs">
              Every active driver / delivery staff who has no shift on {shiftDate}. Doesn't mean absent — could be on leave, off-day, or forgot.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {missing.length === 0 ? (
              <div className="text-center py-6 text-emerald-700 text-sm">🎉 Everyone clocked in.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Assigned Vehicle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missing.map((m) => (
                    <TableRow key={m.driver_id} data-testid={`row-missing-${m.driver_id}`}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{roleBadge(m.role)}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {m.vehicle
                          ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">🚚 {m.vehicle}</span>
                          : <span className="text-slate-400">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Admin: edit shift KM */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-shift-dialog">
          <DialogHeader>
            <DialogTitle>Fix odometer readings</DialogTitle>
            <DialogDescription>
              {editing?.name} · {editing?.vehicle || 'no vehicle'} · {shiftDate}. Adjusting these values recomputes distance and re-syncs the Fleet Maintenance mileage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Start KM</Label>
              <Input
                type="number"
                value={editForm.start_km}
                onChange={(e) => setEditForm({ ...editForm, start_km: e.target.value })}
                placeholder="e.g. 125300"
                data-testid="edit-start-km"
              />
            </div>
            <div>
              <Label className="text-xs">End KM</Label>
              <Input
                type="number"
                value={editForm.end_km}
                onChange={(e) => setEditForm({ ...editForm, end_km: e.target.value })}
                placeholder="Leave blank if shift is still open"
                data-testid="edit-end-km"
              />
            </div>
            <div>
              <Label className="text-xs">Admin note (optional)</Label>
              <Input
                value={editForm.admin_note}
                onChange={(e) => setEditForm({ ...editForm, admin_note: e.target.value })}
                placeholder="Reason for the correction"
                data-testid="edit-admin-note"
              />
            </div>
            {editForm.start_km !== '' && editForm.end_km !== '' && Number(editForm.end_km) >= Number(editForm.start_km) && (
              <div className="text-xs text-emerald-700">
                New distance: <b>{Number(editForm.end_km) - Number(editForm.start_km)} km</b>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-shift-submit">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Tile({ label, value, icon, highlight }) {
  const cls = highlight === 'emerald' ? 'border-emerald-300 bg-emerald-50/40'
            : highlight === 'amber'   ? 'border-amber-300 bg-amber-50/40'
            : highlight === 'rose'    ? 'border-rose-300 bg-rose-50/40'
            : '';
  return (
    <Card className={cls}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">{icon}{label}</div>
        <div className="text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
